import { create } from "zustand";
import { SUBSCRIPTION_PLAN_TYPE } from "@brimble/models";
import { axios_server } from "@/backend";
import { useEffect } from "react";

export type PlanConfiguration = {
  _id: string;
  tag: string;
  slack_support: boolean;
  concurrent_builds: number;
  project_limit: number;
  deploy_private_organization: boolean;
  preview_comments: boolean;
  analytics: boolean;
  build_minutes: number;
  log_retention: number;
  bandwidth: number;
  plan_type: SUBSCRIPTION_PLAN_TYPE;
  memory: number;
  build_timeout: number;
  storage: number;
  cpu: number;
  price: number;
};

interface PlanConfigStore {
  configurations: PlanConfiguration[];
  defaultConfiguration: PlanConfiguration | null;
  userPlanConfiguration: PlanConfiguration | null;
  isLoading: boolean;
  error: Error | null;
  initialized: boolean;

  fetchConfigurations: () => Promise<void>;
  setUserPlanType: (planType: SUBSCRIPTION_PLAN_TYPE) => void;
  getPlanConfiguration: (
    planType: SUBSCRIPTION_PLAN_TYPE,
  ) => PlanConfiguration | null;
}

export const usePlanConfigStore = create<PlanConfigStore>((set, get) => ({
  configurations: [],
  defaultConfiguration: null,
  userPlanConfiguration: null,
  isLoading: false,
  error: null,
  initialized: false,

  fetchConfigurations: async () => {
    if (get().initialized) return;

    set({ isLoading: true, error: null });

    try {
      const response = await axios_server.get("/v1/plan-configurations");
      const data = response.data.data.configurations;
      let defaultConfig = response.data.data.defaultConfiguration;

      if (defaultConfig?.plan_type === SUBSCRIPTION_PLAN_TYPE.TeamPlan) {
        const teamConfig = data.find(
          (plan: PlanConfiguration) =>
            plan.tag === "team" ||
            plan.plan_type === SUBSCRIPTION_PLAN_TYPE.TeamPlan,
        );
        if (teamConfig) {
          defaultConfig = teamConfig;
        }
      }

      set({
        configurations: data,
        defaultConfiguration: defaultConfig,
        isLoading: false,
        initialized: true,
      });
    } catch (error) {
      set({
        error: error as Error,
        isLoading: false,
      });
    }
  },

  setUserPlanType: (planType: SUBSCRIPTION_PLAN_TYPE) => {
    const { configurations } = get();
    const userConfig =
      configurations.find((plan) => plan.plan_type === planType) || null;

    set({ userPlanConfiguration: userConfig });
  },

  getPlanConfiguration: (planType: SUBSCRIPTION_PLAN_TYPE) => {
    const { configurations, defaultConfiguration } = get();

    if (planType === SUBSCRIPTION_PLAN_TYPE.TeamPlan) {
      const teamConfig = configurations.find(
        (plan) =>
          plan.tag === "team" ||
          plan.plan_type === SUBSCRIPTION_PLAN_TYPE.TeamPlan,
      );
      return teamConfig || defaultConfiguration;
    }

    const config =
      configurations.find((plan) => plan.plan_type === planType) || null;
    return config || defaultConfiguration;
  },
}));

export const usePlanConfig = (planType?: SUBSCRIPTION_PLAN_TYPE) => {
  const {
    initialized,
    isLoading,
    fetchConfigurations,
    setUserPlanType,
    getPlanConfiguration,
    userPlanConfiguration,
    defaultConfiguration,
    ...store
  } = usePlanConfigStore();

  useEffect(() => {
    if (!initialized && !isLoading) {
      fetchConfigurations();
    }
  }, [initialized, isLoading, fetchConfigurations]);

  useEffect(() => {
    if (planType && initialized) {
      setUserPlanType(planType);
    }
  }, [planType, initialized, setUserPlanType]);

  return {
    ...store,
    initialized,
    isLoading,
    fetchConfigurations,
    setUserPlanType,
    getPlanConfiguration,
    userPlanConfiguration,
    defaultConfiguration,
    planConfiguration: planType
      ? getPlanConfiguration(planType)
      : userPlanConfiguration || defaultConfiguration,
  };
};
